import { useMemo } from 'react'
import {
  acceptAnalyticsConsent,
  getAnalyticsConsentStatus,
  rejectAnalyticsConsent,
} from '@/features/privacy/consent'

type CookiePanelProps = {
  isOpen: boolean
  onClose: () => void
}

export function CookiePanel({ isOpen, onClose }: CookiePanelProps) {
  const status = getAnalyticsConsentStatus()
  const policyLastUpdated = '2026-06-08'

  const statusLabel = useMemo(() => {
    if (status === 'accepted') return 'Accepted'
    if (status === 'rejected') return 'Rejected'
    return 'Not selected'
  }, [status])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Cookie settings and policy"
    >
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-[1.75rem] border border-edge/70 bg-surface p-6 shadow-panel sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="eyebrow text-accent">NEENO Lab</p>
            <h2 className="mt-1.5 text-lg font-medium tracking-tight text-primary sm:text-xl">
              Cookie Settings & Policy
            </h2>
            <p className="mt-1 text-sm text-muted">
              Manage optional analytics cookies and review how cookies are used on this site.
            </p>
          </div>
          <button type="button" onClick={onClose} className="pill shrink-0">
            Close
          </button>
        </div>

        <section className="mt-8 rounded-2xl border border-edge/60 bg-canvas/40 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold tracking-tight text-primary">
                Analytics cookie consent
              </p>
              <p className="text-xs text-muted">Current status: {statusLabel}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={rejectAnalyticsConsent} className="pill">
                Reject analytics
              </button>
              <button type="button" onClick={acceptAnalyticsConsent} className="pill-solid">
                Accept analytics
              </button>
            </div>
          </div>
        </section>

        <section className="mt-8 space-y-3">
          <h3 className="text-sm font-semibold tracking-tight text-primary">What are cookies?</h3>
          <p className="text-sm text-muted">
            Cookies are small text files stored by your browser. They can remember preferences,
            distinguish sessions, and support aggregate analytics.
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h3 className="text-sm font-semibold tracking-tight text-primary">
            Types of cookies used on this site
          </h3>
          <p className="text-sm text-muted">
            Essential cookies: none currently set by this site for basic operation.
          </p>
          <p className="text-sm text-muted">
            Optional analytics cookies: set only after explicit consent for Google Analytics. No
            marketing or profiling cookies are currently used.
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h3 className="text-sm font-semibold tracking-tight text-primary">
            Cookie list, names, and purposes
          </h3>
          <div className="overflow-hidden rounded-2xl border border-edge/60">
            <table className="w-full border-collapse text-left text-xs">
              <thead className="bg-inset/60 text-primary">
                <tr>
                  <th className="border-b border-edge/60 px-3 py-2.5 font-medium">Cookie</th>
                  <th className="border-b border-edge/60 px-3 py-2.5 font-medium">Type</th>
                  <th className="border-b border-edge/60 px-3 py-2.5 font-medium">Purpose</th>
                  <th className="border-b border-edge/60 px-3 py-2.5 font-medium">Duration</th>
                  <th className="border-b border-edge/60 px-3 py-2.5 font-medium">Provider</th>
                </tr>
              </thead>
              <tbody className="text-muted">
                <tr>
                  <td className="border-b border-edge/60 px-3 py-2.5 font-mono text-[11px] text-primary">
                    _ga
                  </td>
                  <td className="border-b border-edge/60 px-3 py-2.5">Analytics</td>
                  <td className="border-b border-edge/60 px-3 py-2.5">
                    Distinguishes users for aggregate usage statistics
                  </td>
                  <td className="border-b border-edge/60 px-3 py-2.5">Up to 2 years</td>
                  <td className="border-b border-edge/60 px-3 py-2.5">Google Analytics</td>
                </tr>
                <tr>
                  <td className="px-3 py-2.5 font-mono text-[11px] text-primary">_ga_*</td>
                  <td className="px-3 py-2.5">Analytics</td>
                  <td className="px-3 py-2.5">Session and interaction measurement</td>
                  <td className="px-3 py-2.5">Up to 2 years</td>
                  <td className="px-3 py-2.5">Google Analytics</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-8 space-y-3">
          <h3 className="text-sm font-semibold tracking-tight text-primary">
            Third-party cookies and data sharing
          </h3>
          <p className="text-sm text-muted">
            If analytics consent is enabled, cookie-related data is shared with Google Analytics
            (Google Ireland Limited) as a third-party processor.
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h3 className="text-sm font-semibold tracking-tight text-primary">
            How to change settings, opt out, or revoke consent
          </h3>
          <p className="text-sm text-muted">
            Use the "Accept analytics" and "Reject analytics" buttons in this panel at any time. You
            can additionally delete or block cookies in your browser settings.
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h3 className="text-sm font-semibold tracking-tight text-primary">Contact details</h3>
          <p className="text-sm text-muted">Organization: NEENO / NEENO Lab</p>
          <p className="text-sm text-muted">
            Main site:{' '}
            <a
              href="https://neeno.io"
              target="_blank"
              rel="noreferrer"
              className="text-accent underline underline-offset-2"
            >
              https://neeno.io
            </a>
          </p>
          <p className="text-sm text-muted">
            Contact: via the contact channels published on the main site.
          </p>
        </section>

        <p className="mt-6 text-xs text-muted">Cookie policy effective date: {policyLastUpdated}</p>
      </div>
    </div>
  )
}
